import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Signal,
    signal,
} from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { FormHtml } from 'dataModels/pages/form/form-html';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import {
    selectDropoffCompletePage,
    selectCallbackFormPage,
} from 'sharedStores/drop-off/drop-off.selectors';
import { DropOffPageState } from 'sharedEnums/drop-off-page-state';
import { FormItem } from 'dataModels/pages/form/form-item';
import { FormButton } from 'dataModels/pages/form/form-button';
import { FormInput } from 'dataModels/pages/form/form-input';
import { AppConfigService } from 'sharedServices/app-config.service';
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormGroup,
} from '@angular/forms';
import {
    createFormGroup,
    errorMessageSignal,
} from 'shared/base/generics/helpers/form-value-operations';
import { WhitespaceValidatorDirective } from 'shared/base/generics/directives/white-space-validator.directive';
import { LetDirective } from '@ngrx/component';
import { ContainerComponent } from 'sharedWidgets/container/container.component';
import { InputComponent } from 'sharedTemplates/dumb/input/input.component';
import { NzInputDirective } from 'ng-zorro-antd/input';
import { CharacterOnlyDirective } from 'sharedDirectives/character-only.directive';
import { CellNumberValidatorDirective } from 'sharedDirectives/cell-validator.directive';
import { NumberOnlyDirective } from 'sharedDirectives/number-only.directive';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzWaveDirective } from 'ng-zorro-antd/core/wave';
import { ɵNzTransitionPatchDirective } from 'ng-zorro-antd/core/transition-patch';
import { SkeletonLoaderDirective } from 'sharedDirectives/skeleton-loader.directive';
import { ReplacePipe } from 'sharedPipes/replace.pipe';
import {
    NameSurnameDirective,
    nameSurnameValidator,
} from 'shared/base/generics/directives/name-surname-validator.directive';
import { selectPolicyHolder, selectDropOffData } from 'stores/personal/personal.selectors';
import { DropOffCode } from 'sharedEnums/drop-off-code';
import * as ResumeQuoteSelectors from 'stores/resume-quote/resume-quote.selectors';
import { Router } from '@angular/router';
import { FormText } from 'sharedStores/cms/models/pages/form/form-text';
import { selectQuoteFlowType } from 'stores/quote/quote.selectors';
import { QuoteFlowType } from 'sharedEnums/quote-flow-type';
import { submitCallbackRequest } from 'sharedStores/drop-off/drop-off.actions';
import { QuoteControllerService_Token } from 'shared/base/generics/injection-tokens/dependency-tokens';
import { isNullOrWhitespace } from 'shared/base/generics/helpers/utils';

class PageData {
    btnCallback: FormItem<FormButton>;
    btnCallbackLoading: FormItem<FormButton>;
    txtName: FormItem<FormInput>;
    txtSurname: FormItem<FormInput>;
    txtNumber: FormItem<FormInput>;
    txtContent: FormItem<FormHtml>;
}

class PageCompleteData {
    btnHome: FormItem<FormButton>;
    btnCallMeBackOrLogin: FormItem<FormButton>;
    btnStartNewQuote: FormItem<FormButton>;
    txtContent: FormItem<FormHtml>;
    txtReplaceContent: FormItem<FormText>;
    txtReplaceSaveAndResumeContent: FormItem<FormText>;
    txtReplaceContentHH: FormItem<FormText>;
    txtHeadingWithName: FormItem<FormHtml>;
    txtHeadingWithoutName: FormItem<FormHtml>;
    txtHeadingWithOutThankYou: FormItem<FormHtml>;
    imgDropOff: FormItem<FormHtml>;
    txtUnderage: FormItem<FormHtml>;
}

@Component({
    selector: 'drop-off-page',
    templateUrl: './html/drop-off-page.component.html',
    styleUrls: ['./css/drop-off-page.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        LetDirective,
        ContainerComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        NzInputDirective,
        CharacterOnlyDirective,
        WhitespaceValidatorDirective,
        CellNumberValidatorDirective,
        NumberOnlyDirective,
        NzButtonComponent,
        NzWaveDirective,
        ɵNzTransitionPatchDirective,
        SkeletonLoaderDirective,
        ReplacePipe,
        NameSurnameDirective,
    ],
})
@UntilDestroy()
export class DropOffPageComponent {
    DropOffState = DropOffPageState;
    DropOffCode = DropOffCode;
    dropOffData: PageData;
    dropOffCompleteData: PageCompleteData;
    dataForm: UntypedFormGroup;

    public quoteController = inject(QuoteControllerService_Token);
    policyHolder = this.store$.selectSignal(selectPolicyHolder);
    public dropOffState$ = this.store$.select(selectDropOffData);
    public dropOffStateCode: string;
    isResumeQuote = this.store$.selectSignal(ResumeQuoteSelectors.selectResumeQuotePageState)()
        ?.isResumeQuote;
    quoteFlowType = this.store$.selectSignal(selectQuoteFlowType)();

    txtContentReplaceData = computed(() => {
        const txtReplaceContent =
            this.quoteFlowType == QuoteFlowType.HomeContents
                ? this.dropOffCompleteData?.txtReplaceContentHH
                : this.dropOffCompleteData?.txtReplaceContent;

        return {
            ...this.additionalData,
            contentText: this.isResumeQuote
                ? this.dropOffCompleteData?.txtReplaceSaveAndResumeContent?.details.text
                : txtReplaceContent?.details.text,
        };
    });

    form = {
        firstName: '',
        surname: '',
        contactNumber: '',
    };

    additionalData: any = { name: '' };
    route: string;
    shouldIncludeName: Signal<boolean>;
    firstNameErrorMessage: Signal<string> = signal('');
    surnameErrorMessage: Signal<string> = signal('');
    contactNumberErrorMessage: Signal<string> = signal('');
    isMasked: boolean = true;
    env: any;

    constructor(
        public store$: Store,
        public appConfig: AppConfigService,
        public formBuilder: UntypedFormBuilder,
        private readonly router: Router,
        private meta: Meta,
    ) {
        // Add noindex meta tag to prevent search engine indexing of callback pages
        this.meta.addTag({ name: 'robots', content: 'noindex, nofollow' });

        this.env = this.appConfig.config();
        const assetsUrl = `${this.env.cmsUrl}${this.env.cmsAssetUrl}`;
        this.additionalData = { ...this.additionalData, assetsUrl };
        this.shouldIncludeName = computed(
            () => !isNullOrWhitespace(this.policyHolder()?.person?.firstName),
        );

        this.store$
            .select(selectCallbackFormPage)
            .pipe(untilDestroyed(this))
            .subscribe((page) => {
                this.dropOffData = page?.components[0].formObject;
                if (page) {
                    this.quoteController.updatePageConfig(page);
                }
            });

        this.store$
            .select(selectDropoffCompletePage)
            .pipe(untilDestroyed(this))
            .subscribe((page) => {
                this.dropOffCompleteData = page?.components[0].formObject;
                if (page) {
                    this.quoteController.updatePageConfig(page);
                }
            });

        this.dataForm = createFormGroup(this.formBuilder, [
            this.dropOffData?.txtName?.details,
            this.dropOffData?.txtSurname?.details,
            this.dropOffData?.txtNumber?.details,
        ]);
        //adding additional validator to name and surname fields
        if (this.dropOffData?.txtName?.details?.formControlName) {
            this.dataForm.controls[this.dropOffData.txtName.details.formControlName].addValidators(
                nameSurnameValidator(
                    this.dataForm.controls[this.dropOffData.txtName.details.formControlName],
                ),
            );
        }
        if (this.dropOffData?.txtSurname?.details?.formControlName) {
            this.dataForm.controls[
                this.dropOffData.txtSurname.details.formControlName
            ].addValidators(
                nameSurnameValidator(
                    this.dataForm.controls[this.dropOffData.txtSurname.details.formControlName],
                ),
            );
        }

        this.dropOffState$.pipe(untilDestroyed(this)).subscribe((state) => {
            this.dropOffStateCode = state.dropOffState?.code;

            // Remove form element if state is COMPLETE
            if (state.dropOffPageState === DropOffPageState.COMPLETE) {
                this.removeFormElement();
            }

            if (state.data) {
                this.form = { ...state.data };
                this.additionalData = {
                    ...this.additionalData,
                    ...state.data.additionalData,
                    name: this.form.firstName ?? '',
                };

                this.dataForm.controls?.firstName.setValue(state.data.firstName);
                this.dataForm.controls?.surname.setValue(state.data.surname);
                this.dataForm.controls?.contactNumber.setValue(state.data.contactNumber);

                if (
                    !state.data.contactNumber ||
                    (state.data.contactNumber && !state.data.contactNumber.includes('*'))
                ) {
                    this.isMasked = false;
                }
            }
            this.route = state.route;
        });

        this.dataForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.firstNameErrorMessage = errorMessageSignal(
                this.dropOffData.txtName.details,
                this.dataForm.controls.firstName?.errors,
            );
            this.surnameErrorMessage = errorMessageSignal(
                this.dropOffData.txtSurname.details,
                this.dataForm.controls.surname?.errors,
            );
            this.contactNumberErrorMessage = errorMessageSignal(
                this.dropOffData.txtNumber.details,
                this.dataForm.controls.contactNumber?.errors,
            );
        });
    }

    dropOff(): void {
        if (this.dataForm.invalid) {
            this.quoteController.reset();
            return;
        }

        this.store$.dispatch(
            submitCallbackRequest({
                firstName: this.dataForm.controls.firstName.value,
                surname: this.dataForm.controls.surname.value,
                contactNumber: this.dataForm.controls.contactNumber.value,
                route: this.route ?? this.dropOffData.btnCallback.details.route.pageRoute,
            }),
        );
    }

    goTo(): void {
        switch (this.dropOffStateCode) {
            case DropOffCode.HAS_MULTI_RISK_ITEMS:
            case DropOffCode.OPERATOR_CODE_UNAVAILABLE:
            case DropOffCode.LEAD_BUSY:
                this.route = this.dropOffData.btnCallback.details.route.pageRoute;
                this.dropOff();
                break;
            case DropOffCode.POLICY_EXISTS:
                location.href = `${this.env.brandDashboardLoginUrl}login`;
                break;
            case DropOffCode.SOFT_DELETE:
            case DropOffCode.HARD_DELETE:
                this.router.navigate([
                    this.dropOffCompleteData?.btnStartNewQuote?.details?.route?.baseRoute,
                ]);
                break;
            case DropOffCode.UNDER_AGE_RISK:
                this.route = this.dropOffData.btnCallback.details.route.pageRoute;
                this.dropOff();
                break;
            default:
                this.quoteController.reset();
        }
    }

    getButtonText(): string {
        return (
            this.dropOffCompleteData?.btnHome?.details?.label ??
            this.dropOffCompleteData?.btnCallMeBackOrLogin?.details?.label ??
            this.dropOffCompleteData?.btnStartNewQuote?.details?.label
        );
    }

    removeFormElement(): void {
        const formElements = document.getElementById('dataFormDropOff');
        if (formElements) {
            formElements.style.display = 'none';
        }
    }

    ngOnDestroy(): void {
        // Remove noindex meta tag when leaving callback page to prevent affecting other pages
        this.meta.removeTag('name="robots"');
    }
}
